import { memo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader } from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { 
  ArrowLeft, 
  RefreshCw, 
  Clock, 
  CheckCircle, 
  Mail,
  ListTodo,
  CheckSquare
} from 'lucide-react'
import { useJiraPersonDetail } from './hooks'
import JiraTaskCard from './components/JiraTaskCard'
import JiraStatusBreakdown from './components/JiraStatusBreakdown'
import JiraProjectBreakdown from './components/JiraProjectBreakdown'
import JiraDependencies from './components/JiraDependencies'

const LoadingSkeleton = memo(function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          ))}
        </div>
        <div className="space-y-4">
          <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
        </div>
      </div>
    </div>
  )
})

const JiraPersonDetail = memo(function JiraPersonDetail() {
  const { t } = useTranslation()
  const { accountId } = useParams<{ accountId: string }>()
  const navigate = useNavigate()
  const { data, isLoading, error, refresh } = useJiraPersonDetail(accountId || null)
  const [activeTab, setActiveTab] = useState<'open' | 'completed'>('open')

  const handleBack = () => {
    navigate('/jira')
  }

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Button variant="ghost" onClick={handleBack} leftIcon={<ArrowLeft className="h-4 w-4" />} className="mb-4">
          {t('jira.backToOverview')}
        </Button>
        <LoadingSkeleton />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Button variant="ghost" onClick={handleBack} leftIcon={<ArrowLeft className="h-4 w-4" />} className="mb-4">
          {t('jira.backToOverview')}
        </Button>
        <Card variant="default" padding="lg" className="text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error || 'User not found'}</p>
          <Button onClick={refresh} leftIcon={<RefreshCw className="h-4 w-4" />}>
            {t('common.retry')}
          </Button>
        </Card>
      </div>
    )
  }

  const { user, openTaskCount, completedTaskCount, tasksByStatus, tasksByProject, openTasks, completedTasks, blockedByIssues, blockingIssues } = data
  const avatarUrl = user.avatarUrls?.['48x48']
  const initials = user.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={handleBack} leftIcon={<ArrowLeft className="h-4 w-4" />}>
          {t('jira.backToOverview')}
        </Button>
        <Button variant="secondary" size="sm" onClick={refresh} loading={isLoading} leftIcon={<RefreshCw className="h-4 w-4" />}>
          {t('common.refresh')}
        </Button>
      </div>

      <Card variant="default" padding="lg" className="bg-gradient-to-r from-primary-50 to-white dark:from-primary-900/20 dark:to-gray-800">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-shrink-0">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={user.displayName}
                className="w-16 h-16 rounded-full ring-4 ring-white dark:ring-gray-700 shadow-lg"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white text-xl font-bold shadow-lg">
                {initials}
              </div>
            )}
          </div>

          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{user.displayName}</h1>
            {user.emailAddress && (
              <a
                href={`mailto:${user.emailAddress}`}
                className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
              >
                <Mail className="h-3.5 w-3.5" />
                {user.emailAddress}
              </a>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-white/80 dark:bg-gray-800/80 rounded-lg">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <Clock className="h-4 w-4 text-amber-500" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">{openTaskCount}</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('jira.openTasks')}</p>
            </div>
            <div className="text-center p-3 bg-white/80 dark:bg-gray-800/80 rounded-lg">
              <div className="flex items-center justify-center gap-1.5 mb-1">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-2xl font-bold text-gray-900 dark:text-white">{completedTaskCount}</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('jira.completedTasks')}</p>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card variant="default" padding="md">
            <CardHeader compact>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setActiveTab('open')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'open'
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <ListTodo className="h-4 w-4" />
                  {t('jira.openTasks')} ({openTasks.length})
                </button>
                <button
                  onClick={() => setActiveTab('completed')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === 'completed'
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <CheckSquare className="h-4 w-4" />
                  {t('jira.completedTasks')} ({completedTasks.length})
                </button>
              </div>
            </CardHeader>
            <CardContent compact>
              {activeTab === 'open' ? (
                openTasks.length > 0 ? (
                  <div className="space-y-3">
                    {openTasks.map(task => (
                      <JiraTaskCard key={task.id} task={task} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ListTodo className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">{t('jira.noOpenTasks')}</p>
                  </div>
                )
              ) : (
                completedTasks.length > 0 ? (
                  <div className="space-y-3">
                    {completedTasks.map(task => (
                      <JiraTaskCard key={task.id} task={task} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckSquare className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">{t('jira.noCompletedTasks')}</p>
                  </div>
                )
              )}
            </CardContent>
          </Card>

          <JiraDependencies blockedByIssues={blockedByIssues} blockingIssues={blockingIssues} />
        </div>

        <div className="space-y-6">
          <JiraStatusBreakdown tasksByStatus={tasksByStatus} title={t('jira.statusBreakdown')} />
          <JiraProjectBreakdown tasksByProject={tasksByProject} title={t('jira.projectBreakdown')} />
        </div>
      </div>
    </div>
  )
})

export default JiraPersonDetail

